/**
 * Reine Anteils-/Brutto-Logik für Trainerstunden-Abrechnung (ohne UI, ohne I/O).
 * Von Apps über `@tennis-matches/services/trainerstunden` nutzbar.
 */

import {
  PARTICIPANT_RATE_SETTINGS_SOURCE_CUSTOM,
  resolveEffectiveParticipantHourlyRate,
} from "./participant-rate-semantics.js";
import { computeTrainerSessionParentBillableHourlyRate } from "./trainer-session-rate.js";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function resolveSessionRateContext(session) {
  const s = session && typeof session === "object" ? session : {};
  const payers = payerIdsForSession(s);
  return {
    atDate: String(s.date || "")
      .trim()
      .slice(0, 10),
    targetGroup:
      String(s.target_group ?? s.targetGroup ?? "youth").trim() || "youth",
    trainingType:
      String(s.training_type ?? s.trainingType ?? "single").trim() || "single",
    groupSize: Math.max(payers.length, 1),
  };
}

export function normalizePid(id) {
  return String(id ?? "").trim();
}

export function payerIdsForSession(session) {
  const s = session && typeof session === "object" ? session : {};
  const splitBase = String(s.split_base || s.splitBase || "planned").trim();
  const billingSplitMode = String(
    s.billing_allocation_mode ??
      s.billingAllocationMode ??
      s.billing_split_mode ??
      s.billingSplitMode ??
      "",
  ).trim();
  const effectiveSplitBase =
    billingSplitMode === "season" || billingSplitMode === "flat_planned"
      ? "planned"
      : splitBase;
  const raw =
    effectiveSplitBase === "present"
      ? (s.present_ids ?? s.presentIds)
      : (s.planned_ids ?? s.plannedIds);
  return Array.isArray(raw)
    ? raw
        .map((x) =>
          normalizePid(typeof x === "object" && x?.id != null ? x.id : x),
        )
        .filter(Boolean)
    : [];
}

function resolveSessionCourtFeeEur(session) {
  const s = session && typeof session === "object" ? session : {};
  const raw = s.court_fee_eur ?? s.courtFeeEur;
  if (raw == null || raw === "") return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function resolveSessionGuestFeeEur(session) {
  const s = session && typeof session === "object" ? session : {};
  const raw = s.guest_fee_eur ?? s.guestFeeEur;
  if (raw == null || raw === "") return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function resolveLocationRow(session, opts = {}) {
  const locationId = normalizePid(session?.location_id ?? session?.locationId);
  if (!locationId) return null;
  if (opts?.locationsById instanceof Map) {
    return opts.locationsById.get(locationId) || null;
  }
  if (opts?.locationsById && typeof opts.locationsById === "object") {
    return opts.locationsById[locationId] || null;
  }
  return null;
}

export function resolveSessionLocationTenantId(session, opts = {}) {
  if (opts?.locationTenantId != null && opts.locationTenantId !== "") {
    return normalizePid(opts.locationTenantId);
  }
  const loc = resolveLocationRow(session, opts);
  return normalizePid(loc?.tenant_id ?? loc?.tenantId);
}

function resolvePersonRow(personId, opts = {}) {
  const pid = normalizePid(personId);
  if (
    opts?.person &&
    typeof opts.person === "object" &&
    normalizePid(opts.person.id) === pid
  ) {
    return opts.person;
  }
  if (opts?.peopleById instanceof Map) return opts.peopleById.get(pid) || null;
  if (opts?.peopleById && typeof opts.peopleById === "object") {
    return opts.peopleById[pid] || null;
  }
  return null;
}

/** Nicht-Mitglied am Trainingsort → Gastspielergebühr. */
export function isPersonGuestAtSessionLocation(person, locationTenantId) {
  const locTenant = normalizePid(locationTenantId);
  if (!locTenant) return false;
  const personTenant = normalizePid(person?.tenant_id ?? person?.tenantId);
  if (!personTenant) return false;
  return personTenant !== locTenant;
}

export function countGuestPayersForSession(session, opts = {}) {
  const guestFee = resolveSessionGuestFeeEur(session);
  if (!(guestFee > 0)) return 0;
  const locationTenantId = resolveSessionLocationTenantId(session, opts);
  if (!locationTenantId) return 0;
  let count = 0;
  for (const pid of payerIdsForSession(session)) {
    const person = resolvePersonRow(pid, opts);
    if (isPersonGuestAtSessionLocation(person, locationTenantId)) count += 1;
  }
  return count;
}

export function grossSessionEur(session, opts = {}) {
  const s = session && typeof session === "object" ? session : {};
  const courtFee = resolveSessionCourtFeeEur(s);
  const guestTotal =
    resolveSessionGuestFeeEur(s) * countGuestPayersForSession(s, opts);
  const pt = String(s.pricing_type || s.pricingType || "hourly_split").trim();
  if (pt === "fixed_total")
    return (
      (Number(s.fixed_total_eur ?? s.fixedTotalEur ?? 0) || 0) +
      courtFee +
      guestTotal
    );
  const hours = (Number(s.duration_min ?? s.durationMin) || 0) / 60;
  const rate = Number(s.rate_eur_per_hour ?? s.rateEurPerHour) || 0;
  if (pt === "hourly_per_participant") {
    const n = payerIdsForSession(s).length || 1;
    return hours * rate * n + courtFee + guestTotal;
  }
  return hours * rate + courtFee + guestTotal;
}

function withCourtFeeShare(result, courtFeeEur, payerCount) {
  const n = Math.max(1, Number(payerCount) || 1);
  const fee = Number(courtFeeEur) || 0;
  const courtFeeShareEur = fee > 0 ? round2(fee / n) : 0;
  return {
    ...result,
    shareEur: round2((Number(result.shareEur) || 0) + courtFeeShareEur),
    courtFeeEur: fee > 0 ? fee : 0,
    courtFeeShareEur,
  };
}

function withGuestFee(result, session, opts, person) {
  const guestFeeEur = resolveSessionGuestFeeEur(session);
  const locationTenantId = resolveSessionLocationTenantId(session, opts);
  const applied =
    guestFeeEur > 0 && isPersonGuestAtSessionLocation(person, locationTenantId)
      ? guestFeeEur
      : 0;
  return {
    ...result,
    shareEur: round2((Number(result.shareEur) || 0) + applied),
    guestFeeEur: guestFeeEur > 0 ? guestFeeEur : 0,
    guestFeeAppliedEur: applied > 0 ? round2(applied) : 0,
  };
}

export function personShareFromSession(personId, session, opts = {}) {
  const s = session && typeof session === "object" ? session : {};
  const payers = payerIdsForSession(s);
  const n = payers.length || 1;
  const pid = normalizePid(personId);
  const pricingType = String(s.pricing_type || s.pricingType || "").trim();
  const courtFeeEur = resolveSessionCourtFeeEur(s);
  const person = resolvePersonRow(pid, opts);
  const emptyFees = {
    courtFeeEur: 0,
    courtFeeShareEur: 0,
    guestFeeEur: 0,
    guestFeeAppliedEur: 0,
  };
  if (!payers.includes(pid)) {
    return {
      shareEur: 0,
      pricingType,
      subsidyShareEur: 0,
      ...emptyFees,
    };
  }
  let baseResult;
  if (pricingType === "fixed_total") {
    const total = Number(s.fixed_total_eur ?? s.fixedTotalEur ?? 0) || 0;
    baseResult = withCourtFeeShare(
      {
        shareEur: total / n,
        pricingType: "fixed_total",
        payerCount: n,
        subsidyShareEur: 0,
      },
      courtFeeEur,
      n,
    );
  } else if (pricingType === "hourly_per_participant") {
    const hours = (Number(s.duration_min ?? s.durationMin) || 0) / 60;
    const rate = Number(s.rate_eur_per_hour ?? s.rateEurPerHour) || 0;
    baseResult = withCourtFeeShare(
      {
        shareEur: round2(hours * rate),
        pricingType: "hourly_per_participant",
        durationMin: Number(s.duration_min ?? s.durationMin) || 0,
        rateEurPerHour: rate,
        grossRateEurPerHour: rate,
        payerCount: n,
        parentBillableRateEurPerHour: rate,
        subsidyRateEurPerHour: 0,
        subsidyShareEur: 0,
      },
      courtFeeEur,
      n,
    );
  } else {
    const hours = (Number(s.duration_min ?? s.durationMin) || 0) / 60;
    const hasExplicitSessionRate =
      s.rate_eur_per_hour != null || s.rateEurPerHour != null;
    let rate = Number(s.rate_eur_per_hour ?? s.rateEurPerHour) || 0;
    const trainersById =
      opts?.trainersById && typeof opts.trainersById === "object"
        ? opts.trainersById
        : null;
    const trainerId = normalizePid(s.trainer_id ?? s.trainerId);
    const trainer = trainerId && trainersById ? trainersById[trainerId] : null;
    const rateCtx = resolveSessionRateContext(s);
    const sessionRateIsExplicitZero = hasExplicitSessionRate && !(rate > 0.004);
    if (
      !sessionRateIsExplicitZero &&
      person &&
      String(person.participant_rate_settings_source || "").trim() ===
        PARTICIPANT_RATE_SETTINGS_SOURCE_CUSTOM
    ) {
      const customRate = resolveEffectiveParticipantHourlyRate(
        person,
        trainer,
        rateCtx,
      );
      if (customRate != null && customRate > 0) {
        rate = customRate;
      }
    }
    const base = {
      shareEur: (hours * rate) / n,
      pricingType: "hourly_split",
      durationMin: Number(s.duration_min ?? s.durationMin) || 0,
      rateEurPerHour: rate,
      grossRateEurPerHour: rate,
      payerCount: n,
      parentBillableRateEurPerHour: rate,
      subsidyRateEurPerHour: 0,
      subsidyShareEur: 0,
    };

    if (!trainer || !(rate > 0.004)) {
      baseResult = withCourtFeeShare(
        { ...base, shareEur: round2(base.shareEur) },
        courtFeeEur,
        n,
      );
    } else {
      const parentRate = computeTrainerSessionParentBillableHourlyRate(
        trainer,
        rateCtx,
      );
      if (
        parentRate == null ||
        !(parentRate >= 0) ||
        parentRate >= rate - 0.004
      ) {
        baseResult = withCourtFeeShare(
          { ...base, shareEur: round2(base.shareEur) },
          courtFeeEur,
          n,
        );
      } else {
        const parentShare = (hours * parentRate) / n;
        const subsidyShare = (hours * (rate - parentRate)) / n;
        baseResult = withCourtFeeShare(
          {
            ...base,
            shareEur: round2(parentShare),
            parentBillableRateEurPerHour: parentRate,
            subsidyRateEurPerHour: round2(rate - parentRate),
            subsidyShareEur: round2(subsidyShare),
          },
          courtFeeEur,
          n,
        );
      }
    }
  }
  return withGuestFee(baseResult, s, opts, person);
}
